// /////////////////////////////////////////////////////////////
// [ファイル] TurquoiseLinkCustomerDialog.tsx
// [概要] ターコイズ取込レビューの「既存顧客へ紐付け」ダイアログ。
//        候補なし (unmatched) の取引先コードを、検索して選んだ既存 CRM 顧客へ
//        コード表 (trn_customer_turquoise_codes) の 1 件として紐付ける。
//        顧客に代表コードが無ければ代表になる。名寄せ行は committed に更新される。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import * as Icons from '../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useAuth } from '../firebase/AuthProvider';
import { useNotification } from '../providers/NotificationProvider';
import { ep } from '../api/endpoints';

const SEARCH_URL = ep('search_crm_customers');
const LINK_URL = ep('link_turquoise_match_to_customer');

interface CustomerOption {
  id: string;
  customer_type: 'individual' | 'corporate';
  display_name: string;
  employer_name?: string | null;
}

export interface TurquoiseLinkTarget {
  match_id: number;
  torihikisaki_cd: string | number;
  torihikisaki_name: string;
  customer_type: 'individual' | 'corporate';
}

interface Props {
  open: boolean;
  target: TurquoiseLinkTarget | null;
  onClose: () => void;
  onLinked: () => void;
}

const TurquoiseLinkCustomerDialog: React.FC<Props> = ({ open, target, onClose, onLinked }) => {
  const { user } = useAuth();
  const notify = useNotification();
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<CustomerOption | null>(null);
  const [saving, setSaving] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);

  useEffect(() => {
    if (open) {
      setOptions([]);
      setSelected(null);
    }
  }, [open]);

  const handleSearch = (query: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!query.trim()) {
      setOptions([]);
      return;
    }
    setSearching(true);
    const seq = ++seqRef.current;
    timerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(SEARCH_URL, { query });
        if (seq !== seqRef.current) return;
        setOptions(res.data.status === 'success' ? res.data.results || [] : []);
      } catch {
        if (seq === seqRef.current) setOptions([]);
      } finally {
        if (seq === seqRef.current) setSearching(false);
      }
    }, 300);
  };

  const handleLink = async () => {
    if (!target || !selected || saving) return;
    setSaving(true);
    try {
      const res = await axios.post(LINK_URL, {
        match_id: target.match_id,
        customer_id: selected.id,
        reviewed_by: user?.email,
      });
      if (res.data.status === 'success') {
        notify.success(`コード ${String(target.torihikisaki_cd).padStart(8, '0')} を「${selected.display_name}」へ紐付けました`);
        onLinked();
        onClose();
      } else {
        notify.error(res.data.message || '紐付けに失敗しました');
      }
    } catch (err: any) {
      notify.error(err?.response?.data?.message || '紐付けに失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const typeMismatch = !!target && !!selected && selected.customer_type !== target.customer_type;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth disableScrollLock>
      <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icons.Link sx={{ fontSize: 18 }} /> 既存顧客へ紐付け
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        {target && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>ターコイズ取引先</Typography>
            <Typography variant="body2">
              <Box component="span" sx={{ fontFamily: 'monospace', mr: 1 }}>{String(target.torihikisaki_cd).padStart(8, '0')}</Box>
              {target.torihikisaki_name}
              <Chip
                label={target.customer_type === 'individual' ? '個人' : '法人'}
                size="small"
                variant="outlined"
                sx={{ ml: 1, height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
              />
            </Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 1 }}>
          このコードを追加する既存の顧客を検索して選んでください。顧客に代表コードが無ければ代表コードになり、あれば「その他のコード」として追加されます。
        </Typography>
        <Autocomplete
          options={options}
          loading={searching}
          value={selected}
          filterOptions={(x) => x}
          getOptionLabel={(o) => (o.employer_name ? `${o.display_name}（${o.employer_name}）` : o.display_name || '')}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          noOptionsText="氏名・会社名で検索してください"
          onInputChange={(_, v, reason) => {
            if (reason === 'input') handleSearch(v);
          }}
          onChange={(_, v) => setSelected(v)}
          renderOption={(props, o) => (
            <li {...props} key={o.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={o.customer_type === 'individual' ? '個人' : '法人'}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                />
                <Typography variant="body2">{o.employer_name ? `${o.display_name}（${o.employer_name}）` : o.display_name}</Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="紐付け先の顧客を検索"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searching ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {typeMismatch && (
          <Alert severity="warning" sx={{ mt: 1.5, fontSize: '0.8rem' }}>
            ターコイズ側の区分と顧客の種別が異なります。コードは種別に関係なく紐付けできますが、相手先が正しいか確認してください。
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>キャンセル</Button>
        <Button onClick={handleLink} disabled={!selected || saving} startIcon={<Icons.Link />} sx={STANDARD_CTA_BUTTON_SX}>
          {saving ? '紐付け中...' : '紐付ける'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TurquoiseLinkCustomerDialog;
